import { IDictionary } from 'util/domain';
import { 
	ItemType,
	toItemCode
} from 'phoenix/domain';
import { AclDatastore } from 'sharebase/dataStores/aclDatastore';
import { 
	IAppAction,
	IMenuItem, 
	AppAction,
	ValidatorPriority
} from 'sharebase/domain';
import { 
	ActionService,
	AuthService 
} from 'phoenix/services';
import { MenuItemFactory } from 'sharebase/factories';

const DEFAULT_NS = 'NONE';
/** 
 * Represents a globally-available registry of action templates
 * to be used for stamping out and transferring menu item instances 
 * for use in various menus and toolbars.
 */
export class ActionRepository {
	public static $inject = [
		'$q',
		'pxAuthService',
		'pxActionService',
		'sbAclDatastore',
		'sbMenuItemFactory'
	];

	private actions:IDictionary<IDictionary<IAppAction>> = {};

	constructor(
		private $q:ng.IQService,
		private pxAuthService:AuthService,
		private pxActionService:ActionService,
		private sbAclDatastore:AclDatastore,
		private sbMenuItemFactory:MenuItemFactory		
	){
	}

	public register(action:IAppAction, namespace?:string):IAppAction[];
	public register(actions:IAppAction[], namespace?:string):IAppAction[];
	public register(actions:IAppAction|IAppAction[], namespace:string = DEFAULT_NS):IAppAction[]{
		if(!(namespace in this.actions)){
			this.actions[namespace] = {};
		}

		if(!Array.isArray(actions)){
			actions = [actions];
		}

		actions = actions.map(a => new AppAction(a));		
		actions.forEach(a => this.actions[namespace][a.action] = a);

		return actions;
	}

	public createFromAction(action:IAppAction, enforceRightsValidation:boolean = true, emitAction:boolean = true):IMenuItem {
		let { label, icon } = action;
		let item = this.sbMenuItemFactory.create(action.action, label, icon);	
		let self = this;

		if(enforceRightsValidation){
			if(angular.isDefined(action.permissions)){
				item.addValidator(enforceRightsValidator);
			}			
		} 

		if(!action.validOnNull){
			item.addValidator(nullTargetValidator);
		}

		if(angular.isDefined(action.allowedTypes) && action.allowedTypes !== ItemType.None){
			item.addValidator(itemTypeValidator, ValidatorPriority.High); 
		}

		if(action.denyLinkUser){
			item.addValidator(denyLinkUserValidator, ValidatorPriority.High);
		}

		if(!action.multiItem){
			item.addValidator(singleItemValidator, ValidatorPriority.High);
		}

		if(emitAction){
			// explicitly not using arrow function so that lexical scope can get
			// bound to the menu item in the validator instead of this service
			item.addTrigger(function(target, $event) {
				return self.pxActionService.emit({
					type:item.action,
					payload:target
				}, $event, this.context);						
			});
		}

		return item;

		function enforceRightsValidator(target) { 
			let targets:any[] = angular.isArray(target) ? target : [target];
			let checks = targets.map(t => self.sbAclDatastore.hasPermission(t, action.permissions));
			return self.$q.all<boolean>(checks).then(perms => perms.every(p => p === true));
		}

		function itemTypeValidator(target){
			let targetType = toItemCode(target);
			return targetType !== ItemType.None && (action.allowedTypes & targetType) !== 0;
		}

		function denyLinkUserValidator(target) {
			return !self.pxAuthService.isShareByLinkUser;
		}

		function singleItemValidator<T>(target:T, all:T[], original:T){
			return !Array.isArray(original);
		}
		
		function nullTargetValidator<T>(target:T, all:T[]){
			return all.length > 0 && angular.isDefined(target) && target !== null;
		}
	}

	public createFromActionName(actionName:string, enforceValidation?:boolean, emitAction?:boolean){
		let action = this.getAction(actionName);
		return this.createFromAction(action, enforceValidation, emitAction);
	}

	/**
	 * Retrieves an action from any namesapces by its name.
	 * @param actionName Action name mapped to IAppAction.
	 */
	public getAction(actionName:string):IAppAction{
		let action:IAppAction;
		let namespaces = Object.keys(this.actions);

		for(var i = 0; i < namespaces.length; i++){
			let actions = this.actions[namespaces[i]];
			if(actionName in actions){
				action = actions[actionName];
				break;
			}		
		}

		return action;
	}
	
	public getActions(namespace?:string):IAppAction[];
	public getActions(actionNames:string[]):IAppAction[];
	public getActions(actionNamesOrNamespace:string[]|string):IAppAction[]{		
		let actions:IAppAction[] = [];		

		// return specified actions across all namespaces
		if(Array.isArray(actionNamesOrNamespace)) {
			let actionNames:string[] = actionNamesOrNamespace;
			actions = actionNames
				.map(actionName => this.getAction(actionName))
				.filter(a => angular.isDefined(a) && a !== null);
		}
		// return all actions associated with a namespace
		else {
			let namespaceId:string = angular.isString(actionNamesOrNamespace) ? actionNamesOrNamespace : DEFAULT_NS;
			if(namespaceId in this.actions){
				let namespace = this.actions[namespaceId];
				actions = Object.keys(namespace).map(k => namespace[k]);
			}
		}

		return actions;
	}
}




// WEBPACK FOOTER //
// ./src/sharebase/services/actionRepository.ts